import BottomSheet from '@components/BottomSheet'
import EmptyList from '@components/EmptyList'
import Prompt from '@components/Prompt'
import { Titlebar } from '@components/Titlebar/Titlebar'
import ApproveCycles from '@modules/ApproveCycles'
import useTopup from '@plug-core/hooks/useTopup'
import { CanisterInfo } from '@plug-core/idls/topUp/topUp'
import { formatNumber } from '@plug-core/utils/formatNumber'
import { Banner, Button, Heading, IconButton, Surface, Text } from '@ui'
import { ROUTES } from '@utils/routes'
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { HiOutlinePlus } from 'react-icons/hi'
import { HiMiniExclamationTriangle } from 'react-icons/hi2'
import { MdHistory } from 'react-icons/md'
import { useNavigate } from 'react-router-dom'
import CanisterCard from './components/CanisterCard'
import CanisterForm from './components/CanisterForm'

export const CanisterTopupPage = () => {
  const [approveVisible, setApproveVisible] = useState(false)
  const [newCanisterFormVisible, setNewCanisterFormVisible] = useState(false)
  const [canisterInEdit, setCanisterInEdit] = useState<CanisterInfo>()

  const {
    profile,
    approvedBalance,
    walletCyclesBalance,
    wallet,
    loading,
    canisterInDelete,
    onCanisterDelete,
    deleteInProgress,
    refreshApproveBalance,
    setCanisterInDelete,
    addCanister,
    updateCanister,
  } = useTopup()

  const navigate = useNavigate()
  const { t } = useTranslation()

  const refreshApproveAndClose = () => {
    setApproveVisible(false)
    refreshApproveBalance()
  }

  const hideCanisterForm = () => {
    setNewCanisterFormVisible(false)
    setCanisterInEdit(undefined)
  }

  return (
    <div className="mb-3 flex flex-col">
      <Titlebar
        onActionClick={() => navigate(-1)}
        leftItem={
          <IconButton
            aria-label={t('topup.topupHistory')}
            className="text-accent"
            onClick={() => {
              navigate(ROUTES.TOPUP_HISTORY, {
                state: { principalId: wallet?.principalId },
              })
            }}
          >
            <MdHistory />
          </IconButton>
        }
      />

      <div className="flex flex-col">
        <Surface padding="none" className="mb-4 flex flex-col justify-center py-4">
          <Text align="center" tone="fg-2">
            {t('topup.tcyclesBalance')}
          </Text>
          <div className="flex items-center">
            <div className="flex flex-1 flex-col items-center justify-center gap-1">
              <Heading level={2} tabular className="mt-1.5">
                {formatNumber(Number(walletCyclesBalance?.balance || 0), undefined, {
                  maximumFractionDigits: 0,
                })}
              </Heading>
              <Text variant="body-s" tone="fg-2">
                {t('topup.plugBalance')}
              </Text>
              <Button variant="ghost" size="sm" onClick={() => navigate(ROUTES.MINT_CYCLES)}>
                Mint
              </Button>
            </div>
            <div className="flex flex-1 flex-col items-center justify-center gap-1">
              <Heading level={2} tabular className="mt-1.5">
                {formatNumber(approvedBalance, undefined, { maximumFractionDigits: 0 })}
              </Heading>
              <Text variant="body-s" tone="fg-2">
                {t('topup.approved')}
              </Text>
              <Button variant="ghost" size="sm" onClick={() => setApproveVisible(true)}>
                Add
              </Button>
            </div>
          </div>
        </Surface>
        <Button
          variant="secondary"
          onClick={() => navigate(ROUTES.SEND, { state: { topup: true } })}
          fullWidth
        >
          {t('topup.topupCanister')}
        </Button>

        {profile?.enough_cycles == false && (
          <Banner tone="info" className="mt-4">
            {t('topup.notEnoughBalance')}
          </Banner>
        )}

        <div className="mt-4 mb-2 flex items-center justify-between">
          <Heading level={4}>{t('topup.savedCanisters')}</Heading>
          <Button
            variant="ghost"
            size="sm"
            startIcon={<HiOutlinePlus />}
            onClick={() => setNewCanisterFormVisible(true)}
          >
            {t('common.add')}
          </Button>
        </div>

        {!loading && !profile?.canisters?.length && (
          <EmptyList
            title={t('topup.noCanisters')}
            description={t('topup.noCanistersDescription')}
          />
        )}

        <div className="flex flex-col gap-2">
          {profile?.canisters?.map((canister, i) => (
            <CanisterCard
              key={i}
              canister={canister}
              onCanisterDelete={() => setCanisterInDelete(canister)}
              onCanisterUpdate={() => setCanisterInEdit(canister)}
            />
          ))}
        </div>

        <Prompt show={approveVisible} close={() => setApproveVisible(false)}>
          <ApproveCycles hideSelf={refreshApproveAndClose} />
        </Prompt>

        <Prompt show={!!canisterInDelete}>
          <div className="flex w-full flex-col items-center">
            <span className="grid size-[50px] place-items-center rounded-full bg-bg text-[30px] text-warning">
              <HiMiniExclamationTriangle aria-hidden="true" />
            </span>
            <div className="flex flex-col items-center">
              <Heading level={4} className="mt-1">
                {t('topup.deleteWarning.title')}
              </Heading>
              <Text align="center" tone="fg-2" className="mt-2 mb-4">
                {t('topup.deleteWarning.description')}
              </Text>
            </div>

            <div className="flex w-full flex-col gap-4">
              <Button
                fullWidth
                onClick={onCanisterDelete}
                variant="danger"
                loading={deleteInProgress}
              >
                {t('common.confirm')}
              </Button>
              <Button fullWidth onClick={() => setCanisterInDelete(undefined)} variant="secondary">
                {t('common.cancel')}
              </Button>
            </div>
          </div>
        </Prompt>

        <BottomSheet
          visible={newCanisterFormVisible || !!canisterInEdit}
          onBackdropClick={hideCanisterForm}
        >
          <CanisterForm
            hideSelf={hideCanisterForm}
            newCanister={newCanisterFormVisible}
            addCanister={addCanister}
            updateCanister={updateCanister}
            canister={canisterInEdit}
          />
        </BottomSheet>
      </div>
    </div>
  )
}
